'use client';

import * as Sentry from '@sentry/nextjs';
import type { IntlError } from 'use-intl';

/**
 * 已上报的 namespace 组合集合，避免同一页面同一 namespace 重复上报。
 * key 格式: `${pathname}|${namespace}`
 */
const reportedKeys = new Set<string>();

/**
 * 初始（路由过滤后的）messages 的顶层 namespace 集合。
 * 由 I18nPreloadProvider 在首次渲染时注册。
 */
const initialNamespaces = new Set<string>();

/**
 * 注册初始 messages 的顶层 namespace。
 * 应在 I18nPreloadProvider 挂载时调用一次。
 */
export function setInitialMessages(messages: Record<string, any>): void {
  if (initialNamespaces.size > 0) return; // 只注册一次
  Object.keys(messages).forEach((key) => initialNamespaces.add(key));
}

/**
 * 追加路由切换时按需加载的 namespace。
 * 由 RouteMessagesProvider 在合并新 messages 时调用。
 */
export function addAvailableNamespaces(messages: Record<string, any>): void {
  Object.keys(messages).forEach((key) => initialNamespaces.add(key));
}

/**
 * next-intl onError 回调：捕获缺失的翻译 key 并上报到 Sentry。
 *
 * 仅处理 MISSING_MESSAGE 类型的错误（即 messages 中找不到对应 key），
 * 其他类型（格式化错误、环境回退等）保持 next-intl 默认行为。
 */
export function onI18nError(error: typeof IntlError.prototype): void {
  // 仅关注缺失 key 的场景
  if (error.code !== 'MISSING_MESSAGE') return;

  // 从错误信息中提取 namespace 和 key
  // next-intl 的错误信息格式: "Could not resolve `key` in namespace `namespace`"
  const namespaceMatch = error.message.match(/in namespace `([^`]+)`/);
  const keyMatch = error.message.match(/resolve `([^`]+)`/);
  const namespace = namespaceMatch?.[1] ?? 'unknown';
  const key = keyMatch?.[1] ?? 'unknown';

  reportMissingNamespace(namespace, key);
}

/**
 * 检查 useTranslations(namespace) 调用是否超出了初始路由过滤的范围。
 * 不受全量包加载影响，即使懒加载组件也能捕获。
 */
export function trackNamespaceAccess(namespace: string | undefined): void {
  if (!namespace) return;

  const topKey = namespace.split('.')[0];

  // 如果初始 messages 中有该顶层 namespace，则视为正常
  if (initialNamespaces.has(topKey)) return;

  reportMissingNamespace(namespace);
}

/**
 * 统一上报缺失的 namespace。
 */
function reportMissingNamespace(namespace: string, key?: string): void {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  // 去重：同一页面同一 namespace 只上报一次
  const dedupeKey = `${pathname}|${namespace}`;
  if (reportedKeys.has(dedupeKey)) return;
  reportedKeys.add(dedupeKey);

  const message = key
    ? `[i18n] MISSING_MESSAGE: ${namespace}.${key}`
    : `[i18n] NAMESPACE_NOT_IN_ROUTE_CONFIG: ${namespace}`;

  Sentry.captureMessage(message, {
    level: 'warning',
    tags: {
      'i18n.namespace': namespace,
    },
    extra: {
      pathname,
      namespace,
      ...(key ? { key } : {}),
    },
  });
}
